import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api/client'
import type { ClassicNextCard, ClassicSession, ClassicSessionProgress, DeckDisplayPreference, DeckKindsResponse, GradeCardResponse, StudyGrade } from '../types'

// ── Session creation ───────────────────────────────────────────────────────────

interface CreateSessionParams {
  deck_id?: string
  share_token?: string
  shuffle?: boolean
  card_ids?: string[]
}

export function useCreateClassicSession() {
  return useMutation<ClassicSession, Error, CreateSessionParams>({
    mutationFn: (body) => api.post('/api/v1/study/sessions', body).then(r => r.data),
  })
}

// ── Session fetch ──────────────────────────────────────────────────────────────

export function useClassicSession(sessionId: string | undefined) {
  return useQuery<ClassicSession>({
    queryKey: ['classic-session', sessionId],
    queryFn: () => api.get(`/api/v1/study/sessions/${sessionId}`).then(r => r.data),
    enabled: !!sessionId,
    staleTime: 0,
  })
}

// ── Next card ─────────────────────────────────────────────────────────────────

export function useNextClassicCard(sessionId: string | undefined) {
  return useQuery<ClassicNextCard | null>({
    queryKey: ['classic-next-card', sessionId],
    queryFn: () => api.get(`/api/v1/study/sessions/${sessionId}/next-card`).then(r => r.data || null),
    enabled: !!sessionId,
    staleTime: 0,
    // Don't retry on 404 (session not found)
    retry: (failCount, error: unknown) => {
      const status = (error as { response?: { status: number } })?.response?.status
      if (status === 404) return false
      return failCount < 2
    },
  })
}

// ── Grade card ────────────────────────────────────────────────────────────────

interface GradeParams {
  sessionId: string
  card_id: string
  rating: StudyGrade
}

export function useGradeClassicCard() {
  const qc = useQueryClient()
  return useMutation<GradeCardResponse, Error, GradeParams>({
    mutationFn: ({ sessionId, card_id, rating }) =>
      api.post(`/api/v1/study/sessions/${sessionId}/grade`, { card_id, rating }).then(r => r.data),
    onSuccess: (data, vars) => {
      // Grade response includes next_card — inject it directly instead of
      // invalidating and re-fetching, which eliminates a full round-trip.
      qc.setQueryData(['classic-next-card', vars.sessionId], data.next_card ?? null)
    },
  })
}

// ── Toggle shuffle ────────────────────────────────────────────────────────────

export function useToggleClassicShuffle() {
  const qc = useQueryClient()
  return useMutation<ClassicSession, Error, string>({
    mutationFn: (sessionId) =>
      api.post(`/api/v1/study/sessions/${sessionId}/toggle-shuffle`).then(r => r.data),
    onSuccess: (data) => {
      qc.setQueryData(['classic-session', data.id], data)
      qc.invalidateQueries({ queryKey: ['classic-next-card', data.id] })
    },
  })
}

// ── Complete session ──────────────────────────────────────────────────────────

export function useCompleteClassicSession() {
  const qc = useQueryClient()
  return useMutation<ClassicSession, Error, string>({
    mutationFn: (sessionId) =>
      api.post(`/api/v1/study/sessions/${sessionId}/complete`).then(r => r.data),
    onSuccess: (data) => {
      qc.setQueryData(['classic-session', data.id], data)
    },
  })
}

// ── Display preference (ADR-039) ─────────────────────────────────────────────

export function useDeckDisplayPreference(deckId: string | undefined) {
  return useQuery<DeckDisplayPreference>({
    queryKey: ['deck-display-pref', deckId],
    queryFn: () =>
      api.get(`/api/v1/decks/${deckId}/display-preference/`).then(r => r.data),
    enabled: !!deckId,
    staleTime: 5 * 60 * 1000,
  })
}

interface UpsertDeckDisplayPrefParams {
  deckId: string
  front_side: 'term-front' | 'definition-front'
  collapsed_parts: string[] | null
}

export function useUpsertDeckDisplayPreference() {
  const qc = useQueryClient()
  return useMutation<DeckDisplayPreference, Error, UpsertDeckDisplayPrefParams>({
    mutationFn: ({ deckId, front_side, collapsed_parts }) =>
      api
        .put(`/api/v1/decks/${deckId}/display-preference/`, { front_side, collapsed_parts })
        .then(r => r.data),
    onSuccess: (data, vars) => {
      qc.setQueryData(['deck-display-pref', vars.deckId], data)
    },
  })
}

// ── Deck kinds with coverage (ADR-040) ───────────────────────────────────────

export function useDeckKinds(deckId: string | undefined) {
  return useQuery<DeckKindsResponse>({
    queryKey: ['deck-kinds', deckId],
    queryFn: () =>
      api.get(`/api/v1/decks/${deckId}/kinds/`).then(r => r.data),
    enabled: !!deckId,
    staleTime: 2 * 60 * 1000,
  })
}

// ── Optimistic one-ahead buffer + serial background grade poster (ADR-038) ──────
//
// Implements docs/diagnostics/classic-optimistic-advance-design.md §5–6.
//
// The Classic queue is static within a session (grading never requeues — only
// shuffle reorders, which bumps the epoch and forces a refetch). So the client
// can safely hold a depth-1 buffer:
//
//   known[0] = card on screen, known[1] = the buffered next card.
//
// On grade we advance INSTANTLY (drop known[0]; the buffered card becomes
// current) and post the grade through a serial FIFO poster — one POST in flight,
// each card posted exactly once, in order. Each grade response refills the buffer
// from its next_card.following_card. The result: no network wait on advance, and
// grading stays never-lost / never-duplicated / never-mis-ordered because the
// poster is serial and the backend grade write is idempotent (conditional UPDATE).

interface GradeJob {
  cardId: string
  rating: StudyGrade
  epoch: number
  attempts: number
}

interface UseClassicBufferOptions {
  onProgress?: (p: ClassicSessionProgress) => void
}

export interface ClassicBuffer {
  current: ClassicNextCard | null
  /** Initial / shuffle (re)load in flight — distinct from a mid-queue underrun, where current is null but loading is false. */
  loading: boolean
  done: boolean
  /** A background grade exhausted its retries or the session went inactive — surfaced, never silently dropped. */
  error: string | null
  /** True while a shuffle drains the poster and refetches; grading is blocked. */
  shuffling: boolean
  /** Advance instantly and enqueue the grade. Returns the graded card id (for the caller's "unknown" tracking) or null if it was a no-op. */
  grade: (rating: StudyGrade) => string | null
  /** Call after toggle-shuffle succeeds: the buffer order is now stale and must be rebuilt. */
  invalidateForShuffle: () => void
  /** Resume the serial poster after a surfaced error (retries the failed grade). */
  retry: () => void
}

const MAX_GRADE_ATTEMPTS = 4
const RETRY_BASE_MS = 400

export function useClassicStudyBuffer(
  sessionId: string | undefined,
  opts: UseClassicBufferOptions = {},
): ClassicBuffer {
  const onProgressRef = useRef(opts.onProgress)
  onProgressRef.current = opts.onProgress

  // known mirrors into a ref so the async poster reads the latest without stale closures.
  const knownRef = useRef<ClassicNextCard[]>([])
  const [known, setKnownState] = useState<ClassicNextCard[]>([])
  const setKnown = useCallback((next: ClassicNextCard[]) => {
    knownRef.current = next
    setKnownState(next)
  }, [])

  const [loading, setLoading] = useState(true)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [shuffling, setShuffling] = useState(false)

  // Every card_id the client has shown/graded this session. The refill dedup
  // diffs against consumed ∪ buffer — never the buffer alone — so a lagging
  // serial poster can never re-inject a card the client already advanced past
  // (the server window is a correct read of still-unreviewed cards and legitimately
  // includes not-yet-committed cards we've consumed). Reset on every reseed/shuffle.
  const consumedRef = useRef<Set<string>>(new Set())

  // Serial FIFO grade poster. The job stays at the front of the queue until it
  // succeeds, so a retry re-posts the same job and ordering is preserved.
  const queueRef = useRef<GradeJob[]>([])
  const inFlightRef = useRef(false)
  // Bumped on every (re)seed and on shuffle. Grade responses carrying a stale
  // epoch may still update progress, but must NOT refill the buffer — the order
  // they describe is no longer valid.
  const epochRef = useRef(0)
  const startedRef = useRef(false)
  const shufflePendingRef = useRef(false)
  const sessionRef = useRef<string | undefined>(sessionId)
  sessionRef.current = sessionId
  // Which session a `done === true` belongs to. `done` is long-lived state reset
  // only in the post-render reseed effect, so on a "study again" reseed it stays
  // true for one render while the page's `phase` has already flipped to studying
  // for the NEW session — the done-detection race that produced the zeroed 0/0/0%
  // results screen. We stamp the session at completion and only surface `done`
  // when that stamp still matches the live session id, so a stale done can never
  // leak across the reseed boundary.
  const doneSessionRef = useRef<string | undefined>(undefined)

  // Forward refs so the mutually-recursive poster/refetch can call each other
  // regardless of declaration order.
  const pumpRef = useRef<() => void>(() => {})
  const refetchRef = useRef<(epoch: number) => Promise<void>>(async () => {})

  const maybeFinish = useCallback(() => {
    if (
      startedRef.current &&
      queueRef.current.length === 0 &&
      !inFlightRef.current &&
      knownRef.current.length === 0 &&
      !shufflePendingRef.current
    ) {
      doneSessionRef.current = sessionRef.current
      setDone(true)
    }
  }, [])

  const refetch = useCallback(async (epoch: number) => {
    const sid = sessionRef.current
    if (!sid) return
    try {
      const r = await api.get(`/api/v1/study/sessions/${sid}/next-card`)
      if (epoch !== epochRef.current) return // superseded by a newer reseed/shuffle
      const card = (r.data || null) as ClassicNextCard | null
      if (!card) {
        setKnown([])
      } else {
        // Seed the full look-ahead window: current card + upcoming (up to CLASSIC_LOOK_AHEAD).
        setKnown([card, ...(card.upcoming ?? [])])
      }
      startedRef.current = true
      setShuffling(false)
      setLoading(false)
      maybeFinish()
    } catch {
      if (epoch !== epochRef.current) return
      setLoading(false)
      setShuffling(false)
      setError('Could not load the next card.')
    }
  }, [setKnown, maybeFinish])
  refetchRef.current = refetch

  // After the poster goes idle, run a pending shuffle-refetch, else check for completion.
  const handleSettle = useCallback(() => {
    if (shufflePendingRef.current && queueRef.current.length === 0 && !inFlightRef.current) {
      shufflePendingRef.current = false
      void refetchRef.current(epochRef.current)
      return
    }
    maybeFinish()
  }, [maybeFinish])

  const pump = useCallback(() => {
    if (inFlightRef.current) return
    const job = queueRef.current[0]
    if (!job) { handleSettle(); return }
    inFlightRef.current = true
    api.post(`/api/v1/study/sessions/${sessionRef.current}/grade`, {
      card_id: job.cardId,
      rating: job.rating,
    })
      .then((r) => {
        queueRef.current = queueRef.current.slice(1)
        inFlightRef.current = false
        const data = r.data as GradeCardResponse
        if (data?.progress) onProgressRef.current?.(data.progress)
        // Refill the buffer only if this response still describes the live order.
        if (job.epoch === epochRef.current) {
          // Build the full response window: next_card + its upcoming list.
          const responseWindow: ClassicNextCard[] = data?.next_card
            ? [data.next_card, ...(data.next_card.upcoming ?? [])]
            : []
          if (responseWindow.length > 0) {
            // Under rapid grading the buffer has already shed the cards the user
            // advanced past, and the server window (a correct read of still-
            // unreviewed cards) re-lists them while their grades are mid-flight.
            // Diff against consumed ∪ buffer — never the buffer alone — so an
            // already-advanced card is never re-injected, while genuinely-new
            // cards still flow in (order preserved).
            const inBuffer = new Set(knownRef.current.map((c) => c.card_id))
            const newTail = responseWindow.filter(
              (c) => !consumedRef.current.has(c.card_id) && !inBuffer.has(c.card_id),
            )
            if (newTail.length > 0) {
              setKnown([...knownRef.current, ...newTail])
            }
          }
        }
        pumpRef.current()
      })
      .catch((err: unknown) => {
        const httpStatus = (err as { response?: { status?: number } })?.response?.status
        if (httpStatus === 404) {
          // Benign: the card is already reviewed server-side, so our local advance
          // was correct. With exactly-once posting this should never be self-inflicted;
          // drop the job and carry on. (Design §5 Hazard A.)
          queueRef.current = queueRef.current.slice(1)
          inFlightRef.current = false
          pumpRef.current()
          return
        }
        if (httpStatus === 409) {
          // Session no longer active — fatal. Surface; never navigate(-1) (rule 13).
          inFlightRef.current = false
          setError('This study session is no longer active.')
          return
        }
        // Network / 5xx — retry the SAME job. The backend grade write is idempotent
        // (conditional UPDATE), so a retry can never double-count. Bounded backoff.
        job.attempts += 1
        inFlightRef.current = false
        if (job.attempts >= MAX_GRADE_ATTEMPTS) {
          setError('Could not save your last answer. Check your connection.')
          return
        }
        setTimeout(() => pumpRef.current(), RETRY_BASE_MS * job.attempts)
      })
  }, [handleSettle, setKnown])
  pumpRef.current = pump

  // (Re)seed whenever the session id changes — new session or "study again".
  useEffect(() => {
    if (!sessionId) return
    const epoch = ++epochRef.current
    queueRef.current = []
    inFlightRef.current = false
    shufflePendingRef.current = false
    startedRef.current = false
    consumedRef.current = new Set()
    doneSessionRef.current = undefined
    setError(null)
    setDone(false)
    setShuffling(false)
    setLoading(true)
    setKnown([])
    void refetch(epoch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  const grade = useCallback((rating: StudyGrade): string | null => {
    if (shuffling || error) return null
    const cur = knownRef.current[0]
    if (!cur) return null
    // Advance instantly: drop the on-screen card; the buffered next becomes current.
    // Record it as consumed so a lagging server window can never re-inject it.
    consumedRef.current.add(cur.card_id)
    setKnown(knownRef.current.slice(1))
    queueRef.current = [...queueRef.current, {
      cardId: cur.card_id, rating, epoch: epochRef.current, attempts: 0,
    }]
    pump()
    return cur.card_id
  }, [shuffling, error, setKnown, pump])

  const invalidateForShuffle = useCallback(() => {
    // Shuffle reorders the server's unreviewed queue, so the depth-1 buffer (and
    // any in-flight following_card) is stale. Bump the epoch to neutralise stale
    // refills, block input, and once the poster drains, refetch the fresh order.
    // Clear the consumed set too: the reshuffled queue must be free to surface any
    // still-unreviewed card, and a stale consumed entry would wrongly filter it.
    epochRef.current += 1
    shufflePendingRef.current = true
    consumedRef.current = new Set()
    setShuffling(true)
    setLoading(true)
    setKnown([])
    handleSettle() // refetch now if the poster is already idle
  }, [setKnown, handleSettle])

  const retry = useCallback(() => {
    setError(null)
    if (queueRef.current.length > 0) {
      // Grade-post failure: re-post the stuck job. The buffer already advanced
      // locally and stays intact — we only failed to persist, never to display.
      queueRef.current[0].attempts = 0
      inFlightRef.current = false
      pump()
    } else {
      // Initial / shuffle load failure: nothing to re-post — refetch the card.
      const epoch = ++epochRef.current
      setLoading(true)
      setKnown([])
      void refetch(epoch)
    }
  }, [pump, refetch, setKnown])

  return {
    current: known[0] ?? null,
    loading,
    // Only surface completion for the session it was computed for — a stale
    // `done` from the just-finished session must not read true during the one
    // render where the page has already advanced to the reseeded session.
    done: done && doneSessionRef.current === sessionRef.current,
    error,
    shuffling,
    grade,
    invalidateForShuffle,
    retry,
  }
}
